import { DateTimeISO, PaginatedResponse, User } from './common';

export type RecipientStatus = 'active' | 'deleted';

export type RecipientRequestStatus =
  | 'needs approval'
  | 'approved'
  | 'declined'
  | 'expired'
  | 'canceled';

export interface RecipientRequestPreview {
  uuid: string;
  status: RecipientRequestStatus;
  recipientNickName: string;
  accountName: string;
  accountLast4Digits: string;
  bankName: string;
  approvedAt?: DateTimeISO;
  declinedAt?: DateTimeISO;
  canceledAt?: DateTimeISO;
  expirationAt?: DateTimeISO;
  requestedAt: DateTimeISO;
  requestedByUserGroup: string;
  requestedBy: User;
}

export interface RecipientRequestDetail extends RecipientRequestPreview {
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  bankLogoUrl: string;
  bankAddressLine1: string;
  bankAddressLine2: string;
  bankAddressCity: string;
  bankAddressState?: string;
  bankAddressPostalCode: string;
  bankAddressCountryCode: string;
  currentAddressLine1: string;
  currentAddressLine2: string;
  currentAddressCity: string;
  currentAddressState?: string;
  currentAddressPostalCode: string;
  currentAddressCountryCode: string;
  approvedBy?: User;
  declinedBy?: User;
  canceledBy?: User;
}

export type RecipientRequestPreviewListResponse =
  PaginatedResponse<RecipientRequestPreview>;

export interface RecipientPreview {
  uuid: string;
  status: RecipientStatus;
  recipientNickName: string;
  accountName: string;
  accountLast4Digits: string;
  bankName: string;
  activatedAt: DateTimeISO;
  deletedAt: DateTimeISO;
  bankCountryCode: string;
  bankLogoUrl: string;
}

export interface RecipientDetail extends RecipientPreview {
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  bankAddressLine1: string;
  bankAddressLine2: string;
  bankAddressCity: string;
  bankAddressState?: string;
  bankAddressPostalCode: string;
  bankAddressCountryCode: string;
  currentAddressLine1: string;
  currentAddressLine2: string;
  currentAddressCity: string;
  currentAddressState?: string;
  currentAddressPostalCode: string;
  currentAddressCountryCode: string;
  activatedBy: User;
  deletedBy: User | null;
}

export type RecipientPreviewListResponse = PaginatedResponse<RecipientPreview>;

export type CreateRecipientRequest = {
  consumerId: string;
  recipientNickName: string;
  accountName: string;
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  bankName: string;
  bankAddressLine1: string;
  bankAddressLine2: string;
  bankAddressCity: string;
  bankAddressState?: string;
  bankAddressPostalCode: string;
  bankAddressCountryCode: string;
  currentAddressLine1: string;
  currentAddressLine2: string;
  currentAddressCity: string;
  currentAddressState: string;
  currentAddressPostalCode: string;
  currentAddressCountryCode: string;
};

export interface AddWireRecipientForm {
  customerId: string;
  recipientInfo: {
    nickName: string;
    name: string;
    internationalAddress: boolean;
    line1: string;
    line2: string;
    city: string;
    state?: string;
    zip: string;
    country: string;
  };
  bankInfo: {
    internationalBank: boolean;
    bankName: string;
    accountType: string;
    accountNumber: string;
    confirmAccountNumber: string;
    routingNumber: string;
    confirmRoutingNumber: string;
    line1: string;
    line2: string;
    city: string;
    state?: string;
    zip: string;
    country: string;
  };
}

export type WireRecipientListQueryParams = {
  consumer_id: string;
  status: RecipientStatus;
  page: number;
  records_per_page?: number;
};

export type WireRecipientRequestListQueryParams = {
  consumer_id: string;
  status: RecipientRequestStatus;
  page: number;
};

export const queryKeys = {
  recipientsList: ['wireRecipients'] as const,
  requestList: ['wireRequests'] as const,
  recipientsDetails: ['wireRecipientsDetails'] as const,
  requestDetails: ['wireRequestsDetails'] as const,
};
